import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
/**
 * Shared image field for template forms.
 *
 * UX mirrors the clip-video dragger: a dashed drop zone with Hebrew
 * copy, a progress state, and on success a thumbnail preview with
 * remove / replace controls. A small secondary toggle "הדבקת קישור
 * ידנית" reveals the legacy URL input for pasting a public URL from
 * outside the system.
 *
 * The value is a plain string (public URL) so the form's `image_url`
 * field semantics are unchanged — this is the same stored column the
 * manual input used to write to.
 */
import React, { useState } from 'react';
import { Alert, Button, Image, Input, Progress, Space, Typography, Upload } from 'antd';
import { DeleteOutlined, InboxOutlined, LinkOutlined, UploadOutlined } from '@ant-design/icons';
import { ApiError } from '../../api/client';
import { uploadImage } from '../../api';
const { Text } = Typography;
const ALLOWED_MIME = new Set(['image/png', 'image/jpeg', 'image/webp', 'image/gif']);
const MAX_BYTES = 5 * 1024 * 1024;
/**
 * Props are optional so AntD's `<Form.Item name="image_url">` can
 * inject value + onChange via React.cloneElement — this is the standard
 * antd pattern for custom controlled form fields.
 */
export function TemplateImageField({ value, onChange } = {}) {
    const emit = (next) => onChange?.(next);
    const [uploading, setUploading] = useState(false);
    const [progress, setProgress] = useState(0);
    const [error, setError] = useState(null);
    const [manualMode, setManualMode] = useState(false);
    const hasImage = typeof value === 'string' && value.trim().length > 0;
    const doUpload = async (file) => {
        setError(null);
        setProgress(0);
        setUploading(true);
        // We don't get real byte-level progress from fetch(); step the bar
        // to give a sense of life. Keeps consistent with the clip uploader
        // for operators who already know the pattern.
        const tick = window.setInterval(() => {
            setProgress((p) => (p < 90 ? p + 10 : p));
        }, 180);
        try {
            const r = await uploadImage(file);
            setProgress(100);
            emit(r.url);
        }
        catch (err) {
            if (err instanceof ApiError) {
                setError(err.message || hebrewFallback(err.code));
            }
            else {
                setError(err.message || 'שגיאה בהעלאת הקובץ');
            }
        }
        finally {
            window.clearInterval(tick);
            setUploading(false);
        }
    };
    const beforeUpload = async (file) => {
        setError(null);
        if (!ALLOWED_MIME.has(file.type)) {
            setError('סוג קובץ לא נתמך. ניתן להעלות תמונה בפורמט PNG, JPEG, WEBP או GIF.');
            return Upload.LIST_IGNORE;
        }
        if (file.size > MAX_BYTES) {
            setError('הקובץ חורג מהגודל המרבי (5MB). יש להעלות קובץ קטן יותר.');
            return Upload.LIST_IGNORE;
        }
        void doUpload(file);
        return Upload.LIST_IGNORE;
    };
    if (hasImage) {
        return (_jsxs(Space, { direction: "vertical", size: 8, style: { width: '100%' }, children: [_jsxs("div", { style: {
                        display: 'flex', alignItems: 'center', gap: 12,
                        padding: 10, borderRadius: 10, border: '1px solid #f0f0f0', background: '#fafafa',
                    }, children: [_jsx(Image, { src: value, alt: "", width: 72, height: 72, style: { objectFit: 'cover', borderRadius: 8, border: '1px solid #eee', background: '#fff' }, preview: { mask: 'תצוגה מקדימה' } }), _jsxs("div", { style: { minWidth: 0, flex: 1 }, children: [_jsx(Text, { ellipsis: true, style: { display: 'block', fontSize: 12 }, children: value }), _jsx(Text, { type: "secondary", style: { fontSize: 11 }, children: "\u05EA\u05DE\u05D5\u05E0\u05D4 \u05DE\u05D5\u05E6\u05DE\u05D3\u05EA \u05DC\u05EA\u05D1\u05E0\u05D9\u05EA" })] }), _jsx(Upload, { showUploadList: false, beforeUpload: beforeUpload, accept: "image/png,image/jpeg,image/webp,image/gif", disabled: uploading, children: _jsx(Button, { icon: _jsx(UploadOutlined, {}), loading: uploading, children: "\u05D4\u05D7\u05DC\u05E3" }) }), _jsx(Button, { danger: true, icon: _jsx(DeleteOutlined, {}), onClick: () => { emit(null); setError(null); }, disabled: uploading, children: "\u05D4\u05E1\u05E8" })] }), error && _jsx(Alert, { type: "error", showIcon: true, message: error }), uploading && _jsx(Progress, { percent: progress, status: "active" })] }));
    }
    return (_jsxs(Space, { direction: "vertical", size: 8, style: { width: '100%' }, children: [!manualMode ? (_jsxs(Upload.Dragger, { name: "file", accept: "image/png,image/jpeg,image/webp,image/gif", multiple: false, maxCount: 1, showUploadList: false, disabled: uploading, beforeUpload: beforeUpload, style: { borderRadius: 12 }, children: [_jsx("p", { className: "ant-upload-drag-icon", style: { marginBottom: 6 }, children: _jsx(InboxOutlined, { style: { color: '#8B2FD6' } }) }), _jsx("p", { className: "ant-upload-text", style: { fontWeight: 600 }, children: "\u05D2\u05E8\u05E8\u05D5 \u05DC\u05DB\u05D0\u05DF \u05EA\u05DE\u05D5\u05E0\u05D4 \u05D0\u05D5 \u05DC\u05D7\u05E6\u05D5 \u05DC\u05D1\u05D7\u05D9\u05E8\u05D4" }), _jsx("p", { className: "ant-upload-hint", style: { fontSize: 12 }, children: "\u05EA\u05DE\u05D5\u05E0\u05D4 \u05D1\u05D5\u05D3\u05D3\u05EA, \u05E2\u05D3 5MB, \u05D1\u05E4\u05D5\u05E8\u05DE\u05D8 PNG, JPEG, WEBP \u05D0\u05D5 GIF." })] })) : (_jsx(Input, { placeholder: "https://...", value: value ?? '', onChange: (e) => emit(e.target.value || null), prefix: _jsx(LinkOutlined, {}) })), uploading && _jsx(Progress, { percent: progress, status: "active" }), error && _jsx(Alert, { type: "error", showIcon: true, message: error }), _jsx(Button, { type: "link", size: "small", style: { padding: 0 }, onClick: () => setManualMode((m) => !m), children: manualMode ? 'חזרה להעלאת תמונה' : 'הדבקת קישור ידנית' })] }));
}
function hebrewFallback(code) {
    switch (code) {
        case 'missing_file': return 'לא נבחר קובץ להעלאה.';
        case 'unsupported_media_type': return 'סוג קובץ לא נתמך.';
        case 'file_too_large': return 'הקובץ חורג מהגודל המרבי.';
        case 'storage_unconfigured': return 'שירות העלאת התמונות אינו מוגדר בשרת.';
        case 'network': return 'שגיאת רשת בהעלאת הקובץ.';
        default: return 'שגיאה בהעלאת הקובץ.';
    }
}
